/**
 * Variant option availability:
 * - Colors: sold-out only when NO size of that color is available
 * - Sizes: sold-out when unavailable for the currently selected color
 * - Visual marks applied with inline !important so theme/Globo CSS cannot wipe them
 */
(function () {
  'use strict';

  const COLOR_OPTION_NAMES = ['color', 'colour', 'צבע', 'couleur', 'colore', 'farbe'];
  const SIZE_OPTION_NAMES = ['size', 'מידה', 'taille', 'taglia', 'größe', 'groesse', 'age'];
  const SIZE_VALUE_RE = /^(xxs|xs|s|m|l|xl|xxl|2xs|2xl|3xl|4xl|age\s*\d+|\d+)$/i;
  const GLOBO_SOLD_OUT_CLASSES = [
    'globo-sold-out-cross-out',
    'globo-sold-out',
    'soldout',
    'sold-out',
    'is-unavailable',
    'unavailable'
  ];

  function normalize(value) {
    return String(value || '')
      .replace(/\u00a0/g, ' ')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function isColorOptionName(name) {
    const n = normalize(name);
    return COLOR_OPTION_NAMES.some((label) => n === label || n.includes(label));
  }

  function isSizeOptionName(name) {
    const n = normalize(name);
    return SIZE_OPTION_NAMES.some((label) => n === label || n.includes(label));
  }

  function getVariantsJson() {
    const scripts = document.querySelectorAll(
      'variant-radios [type="application/json"], variant-selects [type="application/json"], product-info [type="application/json"], [data-product-json]'
    );

    for (const script of scripts) {
      if (script.id && script.id.indexOf('ProductOptions613') === 0) continue;
      try {
        const data = JSON.parse(script.textContent);
        if (Array.isArray(data) && data.length && (data[0].options || data[0].option1 != null)) {
          return data;
        }
        if (data && Array.isArray(data.variants) && data.variants.length) return data.variants;
      } catch (e) {
        /* ignore */
      }
    }

    return null;
  }

  function getProductOptionNames() {
    const scripts = document.querySelectorAll('[id^="ProductOptions613"]');
    for (const script of scripts) {
      try {
        const data = JSON.parse(script.textContent);
        if (Array.isArray(data) && data.length) return data.map(String);
      } catch (e) {
        /* ignore */
      }
    }

    const fieldsets = document.querySelectorAll('variant-radios fieldset.product-form__input');
    if (fieldsets.length) {
      return Array.from(fieldsets).map((fs) => {
        const legend = fs.querySelector('legend');
        const input = fs.querySelector('input[type="radio"]');
        return (legend && legend.textContent.replace(/:.*/, '').trim()) || (input && input.name) || '';
      });
    }

    return [];
  }

  function inferOptionIndexes(variants, optionNames) {
    let colorIndex = -1;
    let sizeIndex = -1;

    optionNames.forEach((name, i) => {
      if (colorIndex < 0 && isColorOptionName(name)) colorIndex = i;
      if (sizeIndex < 0 && isSizeOptionName(name)) sizeIndex = i;
    });

    if (colorIndex < 0 || sizeIndex < 0) {
      const sample = variants[0] ? variantOptions(variants[0]) : [];
      const counts = sample.map((_, idx) => {
        const values = new Set(
          variants.map((v) => normalize(variantOptions(v)[idx])).filter(Boolean)
        );
        let sizeHits = 0;
        values.forEach((v) => {
          if (SIZE_VALUE_RE.test(v)) sizeHits += 1;
        });
        return { idx, sizeHits, total: values.size };
      });

      counts.sort((a, b) => b.sizeHits - a.sizeHits);
      if (sizeIndex < 0 && counts[0] && counts[0].sizeHits > 0) sizeIndex = counts[0].idx;
      if (colorIndex < 0) {
        const other = counts.find((c) => c.idx !== sizeIndex);
        colorIndex = other ? other.idx : sizeIndex === 0 ? 1 : 0;
      }
    }

    if (colorIndex < 0) colorIndex = 0;
    if (sizeIndex < 0) sizeIndex = colorIndex === 0 ? 1 : 0;
    return { colorIndex, sizeIndex };
  }

  function variantOptions(variant) {
    return variant.options || [variant.option1, variant.option2, variant.option3].filter((v) => v != null);
  }

  function buildAvailableColorSet(variants, colorIndex) {
    const available = new Set();
    variants.forEach((variant) => {
      if (!variant.available) return;
      const opts = variantOptions(variant);
      if (opts[colorIndex] != null) available.add(normalize(opts[colorIndex]));
    });
    return available;
  }

  function buildAvailableSizesForColor(variants, colorIndex, sizeIndex, colorValue) {
    const available = new Set();
    const color = normalize(colorValue);

    variants.forEach((variant) => {
      if (!variant.available) return;
      const opts = variantOptions(variant);
      if (normalize(opts[colorIndex]) !== color) return;
      if (opts[sizeIndex] != null) available.add(normalize(opts[sizeIndex]));
    });

    return available;
  }

  function buildAvailableColorsForSize(variants, colorIndex, sizeIndex, sizeValue) {
    const available = new Set();
    const size = normalize(sizeValue);

    variants.forEach((variant) => {
      if (!variant.available) return;
      const opts = variantOptions(variant);
      if (normalize(opts[sizeIndex]) !== size) return;
      if (opts[colorIndex] != null) available.add(normalize(opts[colorIndex]));
    });

    return available;
  }

  function isGloboColorList(list) {
    if (!list) return false;
    if (list.classList.contains('g-variant-color')) return true;
    if (list.closest('.g-variant-color')) return true;

    const wrap = list.closest(
      '.swatch--gl, .swatches-globo > div, .globo-swatch-list, .globo-swatch-product-detail'
    );
    const title = wrap?.querySelector(
      '.swatch-globo-title, .globo-title, .label, .name-option, legend, .form__label, .title, p'
    );

    if (title && isColorOptionName(title.textContent.replace(/:.*/, ''))) return true;
    if (title && isSizeOptionName(title.textContent.replace(/:.*/, ''))) return false;

    const samples = Array.from(list.querySelectorAll('li'))
      .slice(0, 5)
      .map((li) => normalize(getOptionValueFromGloboItem(li)));
    const sizeHits = samples.filter((s) => SIZE_VALUE_RE.test(s)).length;
    if (sizeHits >= 2) return false;

    return Boolean(
      list.querySelector(
        '.globo-border-color-swatch, [style*="background-color"], .swatch_color_prd, [data-613-color-swatch]'
      )
    );
  }

  function getGloboLists() {
    return Array.from(
      document.querySelectorAll(
        '.swatches-globo .ul-swatches-list, .globo-swatch-list ul.value, .globo-swatch-product-detail ul.value, ul.ul-swatches-list'
      )
    );
  }

  function getOptionValueFromGloboItem(item) {
    const fromAttr =
      item.getAttribute('data-value') ||
      item.getAttribute('data-option-value') ||
      item.querySelector('[data-value]')?.getAttribute('data-value') ||
      item.querySelector('input')?.value ||
      item.querySelector('label')?.getAttribute('data-value') ||
      item.querySelector('[title]')?.getAttribute('title') ||
      item.querySelector('[aria-label]')?.getAttribute('aria-label');

    if (fromAttr) return fromAttr;

    const button = item.querySelector(
      '.globo-style--button, label.swatch-anchor, label, a, button, span'
    );
    if (button) {
      const own = Array.from(button.childNodes)
        .filter((n) => n.nodeType === 3)
        .map((n) => n.textContent.trim())
        .filter(Boolean)
        .join(' ');
      if (own) return own;
      return button.getAttribute('title') || button.getAttribute('aria-label') || button.textContent;
    }

    return item.textContent;
  }

  function getSelectedOptionFromLabel(isMatch) {
    const label = Array.from(
      document.querySelectorAll(
        '.swatches-globo .swatch-globo-title, .swatches-globo .globo-title, .globo-swatch-product-detail .label, .swatches-globo .swatch--gl > .title, .swatches-globo .name-option, .swatches-globo .swatch--gl span, .swatches-globo .swatch--gl p, .swatches-globo [class*="title"]'
      )
    ).find((el) => {
      const text = el.textContent || '';
      return text.includes(':') && isMatch(text.replace(/:.*/, ''));
    });

    if (label) return normalize(label.textContent.split(':').slice(1).join(':'));
    return '';
  }

  function getSelectedColorValue(colorIndex) {
    const fieldsets = document.querySelectorAll('variant-radios fieldset.product-form__input');
    if (fieldsets[colorIndex]) {
      const checked = fieldsets[colorIndex].querySelector('input[type="radio"]:checked');
      if (checked) return normalize(checked.value);
    }

    const selects = document.querySelectorAll('variant-selects select');
    if (selects[colorIndex]) return normalize(selects[colorIndex].value);

    for (const list of getGloboLists()) {
      if (!isGloboColorList(list)) continue;
      const selected = list.querySelector(
        'li.active, li.selected, li.globo-current, li.current, .globo-current, [aria-checked="true"]'
      );
      if (selected) {
        const li = selected.closest('li') || selected;
        return normalize(getOptionValueFromGloboItem(li));
      }
    }

    return getSelectedOptionFromLabel(isColorOptionName);
  }

  function getSelectedSizeValue(sizeIndex) {
    const fieldsets = document.querySelectorAll('variant-radios fieldset.product-form__input');
    if (fieldsets[sizeIndex]) {
      const checked = fieldsets[sizeIndex].querySelector('input[type="radio"]:checked');
      if (checked) return normalize(checked.value);
    }

    const selects = document.querySelectorAll('variant-selects select');
    if (selects[sizeIndex]) return normalize(selects[sizeIndex].value);

    for (const list of getGloboLists()) {
      if (isGloboColorList(list)) continue;
      const selected = list.querySelector(
        'li.active, li.selected, li.globo-current, li.current, .globo-current, [aria-checked="true"]'
      );
      if (selected) {
        const li = selected.closest('li') || selected;
        return normalize(getOptionValueFromGloboItem(li));
      }
    }

    return getSelectedOptionFromLabel(isSizeOptionName);
  }

  function firstAvailableColor(variants, colorIndex, preferredSize, sizeIndex) {
    if (preferredSize) {
      const forSize = buildAvailableColorsForSize(variants, colorIndex, sizeIndex, preferredSize);
      if (forSize.size) return Array.from(forSize)[0];
    }
    const any = buildAvailableColorSet(variants, colorIndex);
    return any.size ? Array.from(any)[0] : '';
  }

  function resolveSelectedColor(variants, colorIndex, sizeIndex) {
    let color = getSelectedColorValue(colorIndex);
    if (color) return color;

    const size = getSelectedSizeValue(sizeIndex) || getSelectedOptionFromLabel(isSizeOptionName);
    color = firstAvailableColor(variants, colorIndex, size, sizeIndex);
    if (color) return color;

    const allColors = new Set(
      variants.map((v) => normalize(variantOptions(v)[colorIndex])).filter(Boolean)
    );
    return allColors.size ? Array.from(allColors)[0] : '';
  }

  function clearColorSoldOut(item) {
    GLOBO_SOLD_OUT_CLASSES.forEach((cls) => item.classList.remove(cls));
    item.classList.add('available');
    item.setAttribute('data-613-color-available', 'true');
    item.removeAttribute('data-613-color-soldout');
    item.removeAttribute('data-613-size-soldout');
    item.removeAttribute('aria-disabled');
    item.removeAttribute('disabled');

    item.querySelectorAll('input, button, a, label').forEach((el) => {
      el.removeAttribute('disabled');
      el.removeAttribute('aria-disabled');
      el.removeAttribute('data-613-color-soldout');
      GLOBO_SOLD_OUT_CLASSES.forEach((cls) => el.classList.remove(cls));
    });

    item.querySelectorAll('img.crossed-out, .crossed-out').forEach((img) => {
      img.style.display = 'none';
    });

    clearSoldOutVisual(item);
  }

  function markColorSoldOut(item) {
    item.classList.add('globo-sold-out-cross-out');
    item.classList.remove('available');
    item.removeAttribute('data-613-color-available');
    item.setAttribute('data-613-color-soldout', 'true');
    item.removeAttribute('data-613-size-soldout');
    applyColorSoldOutVisual(item, true);
  }

  function markSizeSoldOut(item, soldOut) {
    if (soldOut) {
      item.classList.add('globo-sold-out-cross-out');
      item.classList.remove('available');
      item.setAttribute('data-613-size-soldout', 'true');
      item.removeAttribute('data-613-color-available');
    } else {
      GLOBO_SOLD_OUT_CLASSES.forEach((cls) => item.classList.remove(cls));
      item.classList.add('available');
      item.removeAttribute('data-613-size-soldout');
    }
  }

  function getSizeButtons(item) {
    const buttons = Array.from(
      item.querySelectorAll(
        'label.globo-style--button, a.globo-style--button, label.swatch-anchor, button.globo-style--button, span.globo-style--button'
      )
    );
    if (buttons.length) return buttons;

    return Array.from(item.children).filter((el) => {
      const tag = el.tagName;
      return tag === 'LABEL' || tag === 'A' || tag === 'BUTTON' || tag === 'SPAN';
    });
  }

  function ensureSlashOverlay(el) {
    let slash = el.querySelector('.js-613-soldout-slash');
    if (!slash) {
      slash = document.createElement('span');
      slash.className = 'js-613-soldout-slash';
      slash.setAttribute('aria-hidden', 'true');
      const computed = window.getComputedStyle(el).position;
      if (computed === 'static') el.style.setProperty('position', 'relative', 'important');
      el.appendChild(slash);
    }
    slash.style.display = 'block';
    return slash;
  }

  function removeSlashOverlay(el) {
    el.querySelectorAll('.js-613-soldout-slash').forEach((node) => node.remove());
  }

  function applySoldOutVisual(el, soldOut, selected) {
    if (!el) return;

    if (soldOut) {
      el.setAttribute('data-613-size-soldout', 'true');
      ensureSlashOverlay(el);

      if (selected) {
        el.style.setProperty('background', '#c4a86a', 'important');
        el.style.setProperty('background-color', '#c4a86a', 'important');
        el.style.setProperty('border-color', '#a8864c', 'important');
        el.style.setProperty('color', '#0a0a0a', 'important');
        el.style.setProperty('-webkit-text-fill-color', '#0a0a0a', 'important');
        el.style.setProperty('opacity', '0.85', 'important');
      } else {
        el.style.setProperty('background', '#e8e8e8', 'important');
        el.style.setProperty('background-color', '#e8e8e8', 'important');
        el.style.setProperty('border-color', 'rgba(168, 134, 76, 0.35)', 'important');
        el.style.setProperty('color', '#6a6a6a', 'important');
        el.style.setProperty('-webkit-text-fill-color', '#6a6a6a', 'important');
        el.style.setProperty('opacity', '0.55', 'important');
        el.style.setProperty('text-decoration', 'line-through', 'important');
      }
      el.style.setProperty('overflow', 'hidden', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
    } else {
      el.removeAttribute('data-613-size-soldout');
      removeSlashOverlay(el);
      if (!selected) {
        el.style.removeProperty('background');
        el.style.removeProperty('background-color');
        el.style.removeProperty('border-color');
        el.style.removeProperty('color');
        el.style.removeProperty('-webkit-text-fill-color');
        el.style.removeProperty('opacity');
        el.style.removeProperty('text-decoration');
        el.style.removeProperty('box-shadow');
      }
    }
  }

  function applyColorSoldOutVisual(item, soldOut) {
    const targets = [item].concat(
      Array.from(
        item.querySelectorAll(
          'label, a, button, span, [data-613-color-swatch], .globo-style--button, .globo-border-color-swatch'
        )
      )
    );

    targets.forEach((el) => {
      if (soldOut) {
        el.setAttribute('data-613-color-soldout', 'true');
        ensureSlashOverlay(el);
        el.style.setProperty('opacity', '0.55', 'important');
        el.style.setProperty('overflow', 'hidden', 'important');
      } else {
        el.removeAttribute('data-613-color-soldout');
        removeSlashOverlay(el);
        el.style.removeProperty('opacity');
      }
    });
  }

  function clearSoldOutVisual(item) {
    applyColorSoldOutVisual(item, false);
    getSizeButtons(item).forEach((btn) => {
      removeSlashOverlay(btn);
      btn.removeAttribute('data-613-size-soldout');
    });
    removeSlashOverlay(item);
  }

  function applySelectedFill(el, selected, soldOut) {
    if (selected) {
      el.setAttribute('data-613-size-selected', 'true');
      const fill = soldOut ? '#c4a86a' : '#a8864c';
      el.style.setProperty('background', fill, 'important');
      el.style.setProperty('background-color', fill, 'important');
      el.style.setProperty('border-color', '#a8864c', 'important');
      el.style.setProperty('color', '#0a0a0a', 'important');
      el.style.setProperty('-webkit-text-fill-color', '#0a0a0a', 'important');
      el.style.setProperty('opacity', soldOut ? '0.85' : '1', 'important');
      el.style.setProperty('box-shadow', 'none', 'important');
      if (!soldOut) el.style.removeProperty('text-decoration');
    } else {
      el.removeAttribute('data-613-size-selected');
      if (!soldOut) {
        el.style.removeProperty('background');
        el.style.removeProperty('background-color');
        el.style.removeProperty('border-color');
        el.style.removeProperty('color');
        el.style.removeProperty('-webkit-text-fill-color');
        el.style.removeProperty('opacity');
        el.style.removeProperty('box-shadow');
        el.style.removeProperty('text-decoration');
      }
    }
  }

  function markSizeSelected(item, selected, soldOut) {
    if (selected) item.setAttribute('data-613-size-selected', 'true');
    else item.removeAttribute('data-613-size-selected');

    const buttons = getSizeButtons(item);
    const targets = buttons.length ? buttons : [item];
    targets.forEach((el) => {
      applySelectedFill(el, selected, soldOut);
      applySoldOutVisual(el, soldOut, selected);
    });
  }

  function fixGloboColorAvailability(availableColors) {
    getGloboLists().forEach((list) => {
      if (!isGloboColorList(list)) return;

      list.querySelectorAll('li.select-option, li').forEach((item) => {
        const value = normalize(getOptionValueFromGloboItem(item));
        if (!value) return;

        if (availableColors.has(value)) {
          clearColorSoldOut(item);
        } else {
          markColorSoldOut(item);
        }
      });
    });
  }

  function fixGloboSizeAvailability(variants, colorIndex, sizeIndex) {
    const selectedColor = resolveSelectedColor(variants, colorIndex, sizeIndex);
    const selectedSize =
      getSelectedSizeValue(sizeIndex) || getSelectedOptionFromLabel(isSizeOptionName);

    const availableSizes = selectedColor
      ? buildAvailableSizesForColor(variants, colorIndex, sizeIndex, selectedColor)
      : new Set();

    getGloboLists().forEach((list) => {
      if (isGloboColorList(list)) return;

      list.querySelectorAll('li.select-option, li').forEach((item) => {
        const value = normalize(getOptionValueFromGloboItem(item));
        if (!value) return;

        // If we have no color context, still mark sizes with zero stock of any color
        let isSoldOut;
        if (selectedColor) {
          isSoldOut = !availableSizes.has(value);
        } else {
          const anyColorHasSize = variants.some((variant) => {
            if (!variant.available) return false;
            return normalize(variantOptions(variant)[sizeIndex]) === value;
          });
          isSoldOut = !anyColorHasSize;
        }

        markSizeSoldOut(item, isSoldOut);
        markSizeSelected(item, Boolean(selectedSize) && value === selectedSize, isSoldOut);
      });
    });
  }

  function fixNativeRadios(variants, colorIndex, sizeIndex, availableColors) {
    const fieldsets = document.querySelectorAll('variant-radios fieldset.product-form__input');

    if (fieldsets[colorIndex]) {
      fieldsets[colorIndex].querySelectorAll('input[type="radio"]').forEach((input) => {
        const available = availableColors.has(normalize(input.value));
        const label = input.nextElementSibling;
        if (available) {
          input.removeAttribute('disabled');
          if (label) {
            label.removeAttribute('data-613-color-soldout');
            removeSlashOverlay(label);
            label.style.removeProperty('opacity');
          }
        } else {
          input.removeAttribute('disabled');
          if (label) {
            label.setAttribute('data-613-color-soldout', 'true');
            ensureSlashOverlay(label);
            label.style.setProperty('opacity', '0.55', 'important');
            label.style.setProperty('overflow', 'hidden', 'important');
            const pos = window.getComputedStyle(label).position;
            if (pos === 'static') label.style.setProperty('position', 'relative', 'important');
          }
        }
      });
    }

    const selectedColor = resolveSelectedColor(variants, colorIndex, sizeIndex);
    if (!selectedColor || !fieldsets[sizeIndex]) return;

    const availableSizes = buildAvailableSizesForColor(variants, colorIndex, sizeIndex, selectedColor);
    fieldsets[sizeIndex].querySelectorAll('input[type="radio"]').forEach((input) => {
      const available = availableSizes.has(normalize(input.value));
      input.removeAttribute('disabled');

      const label = input.nextElementSibling;
      if (label && label.tagName === 'LABEL') {
        const selected = input.checked;
        if (available) {
          label.removeAttribute('data-613-size-soldout');
          applySoldOutVisual(label, false, selected);
          if (selected) applySelectedFill(label, true, false);
        } else {
          label.setAttribute('data-613-size-soldout', 'true');
          applySoldOutVisual(label, true, selected);
          if (selected) applySelectedFill(label, true, true);
        }
      }
    });
  }

  function findAvailableVariantForColor(variants, colorIndex, colorValue, preferredOptions) {
    const preferred = preferredOptions ? preferredOptions.slice() : null;

    if (preferred) {
      const exact = variants.find((variant) => {
        if (!variant.available) return false;
        const opts = variantOptions(variant);
        return (
          normalize(opts[colorIndex]) === colorValue &&
          preferred.every((opt, i) => {
            if (i === colorIndex) return true;
            return !opt || normalize(opts[i]) === normalize(opt);
          })
        );
      });
      if (exact) return exact;
    }

    return variants.find((variant) => {
      if (!variant.available) return false;
      const opts = variantOptions(variant);
      return normalize(opts[colorIndex]) === colorValue;
    });
  }

  function getSelectedOptions(colorIndex, sizeIndex) {
    const radios = document.querySelector('variant-radios');
    if (radios) {
      return Array.from(radios.querySelectorAll('fieldset')).map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        return checked ? checked.value : null;
      });
    }

    const selects = document.querySelector('variant-selects');
    if (selects) {
      return Array.from(selects.querySelectorAll('select')).map((select) => select.value);
    }

    const opts = [];
    opts[colorIndex] = getSelectedColorValue(colorIndex) || null;
    opts[sizeIndex] = getSelectedSizeValue(sizeIndex) || null;
    return opts;
  }

  function selectNativeOptions(options) {
    const radios = document.querySelector('variant-radios');
    if (radios) {
      const fieldsets = Array.from(radios.querySelectorAll('fieldset'));
      options.forEach((value, index) => {
        if (!value || !fieldsets[index]) return;
        const input = Array.from(fieldsets[index].querySelectorAll('input[type="radio"]')).find(
          (el) => normalize(el.value) === normalize(value)
        );
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
      return;
    }

    const selectsRoot = document.querySelector('variant-selects');
    if (selectsRoot) {
      const selects = Array.from(selectsRoot.querySelectorAll('select'));
      options.forEach((value, index) => {
        if (!value || !selects[index]) return;
        if (normalize(selects[index].value) !== normalize(value)) {
          selects[index].value = value;
          selects[index].dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    }
  }

  function clickGloboOption(list, value) {
    const item = Array.from(list.querySelectorAll('li.select-option, li')).find(
      (li) => normalize(getOptionValueFromGloboItem(li)) === normalize(value)
    );
    const clickable = item?.querySelector('label, a, button, input') || item;
    if (clickable) clickable.click();
  }

  let autoSelectAttempts = 0;
  function ensureGloboColorSelected(variants, colorIndex, sizeIndex) {
    if (getSelectedColorValue(colorIndex)) return;
    if (autoSelectAttempts >= 8) return;
    autoSelectAttempts += 1;

    const color = resolveSelectedColor(variants, colorIndex, sizeIndex);
    if (!color) return;

    getGloboLists().forEach((list) => {
      if (!isGloboColorList(list)) return;
      clickGloboOption(list, color);
    });

    const options = getSelectedOptions(colorIndex, sizeIndex);
    options[colorIndex] = color;
    selectNativeOptions(options);
  }

  function wireColorClickSync(variants, colorIndex, sizeIndex) {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest(
          '.swatches-globo .g-variant-color li, .swatches-globo li.select-option, .swatches-globo .globo-border-color-swatch, .swatches-globo [data-613-color-swatch], .product__info-container .swatches-globo li'
        );
        if (!target || !target.closest('.product__info-container, .swatches-globo')) return;

        const list = target.closest('.ul-swatches-list, ul.value, .g-variant-color');
        if (list && !isGloboColorList(list) && !list.classList.contains('g-variant-color')) {
          // Might still be a color list without the class — check by values
          const samples = Array.from(list.querySelectorAll('li'))
            .slice(0, 4)
            .map((li) => normalize(getOptionValueFromGloboItem(li)));
          const sizeHits = samples.filter((s) => SIZE_VALUE_RE.test(s)).length;
          if (sizeHits >= 2) return;
        }

        const li = target.closest('li') || target;
        const value = normalize(getOptionValueFromGloboItem(li));
        if (!value) return;

        window.setTimeout(() => {
          const options = getSelectedOptions(colorIndex, sizeIndex);
          options[colorIndex] = value;
          selectNativeOptions(options);

          // Make sure Globo marks this as selected
          getGloboLists().forEach((globoList) => {
            if (!isGloboColorList(globoList)) return;
            globoList.querySelectorAll('li').forEach((item) => {
              const match = normalize(getOptionValueFromGloboItem(item)) === value;
              item.classList.toggle('active', match);
              item.classList.toggle('selected', match);
              item.classList.toggle('globo-current', match);
            });
          });
        }, 10);
      },
      true
    );
  }

  function autoSwitchSizeWhenColorSelected(variants, colorIndex, sizeIndex) {
    document.addEventListener(
      'click',
      (event) => {
        const target = event.target.closest(
          '.swatches-globo .ul-swatches-list li, .globo-swatch-list li, variant-radios fieldset label, variant-radios input[type="radio"]'
        );
        if (!target) return;

        const list = target.closest('.ul-swatches-list, ul.value');
        const fieldset = target.closest('fieldset.product-form__input');
        const isColorClick =
          (list && isGloboColorList(list)) ||
          (fieldset &&
            Array.from(document.querySelectorAll('variant-radios fieldset')).indexOf(fieldset) ===
              colorIndex);

        if (!isColorClick) return;

        window.setTimeout(() => {
          const selected = getSelectedOptions(colorIndex, sizeIndex);
          const colorValue = normalize(selected[colorIndex] || getSelectedColorValue(colorIndex));
          if (!colorValue) return;

          const current = variants.find((variant) => {
            const opts = variantOptions(variant);
            return opts.every((opt, i) => normalize(opt) === normalize(selected[i]));
          });

          if (current && current.available) return;

          const fallback = findAvailableVariantForColor(variants, colorIndex, colorValue, selected);
          if (!fallback) return;

          const opts = variantOptions(fallback);
          selectNativeOptions(opts);

          getGloboLists().forEach((sizeList) => {
            if (isGloboColorList(sizeList)) return;
            opts.forEach((opt, i) => {
              if (i === colorIndex) return;
              clickGloboOption(sizeList, opt);
            });
          });
        }, 50);
      },
      true
    );
  }

  let scheduled = false;
  function scheduleFix(variants, colorIndex, sizeIndex, availableColors) {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      ensureGloboColorSelected(variants, colorIndex, sizeIndex);
      fixGloboColorAvailability(availableColors);
      fixGloboSizeAvailability(variants, colorIndex, sizeIndex);
      fixNativeRadios(variants, colorIndex, sizeIndex, availableColors);
    });
  }

  function init() {
    const variants = getVariantsJson();
    if (!variants || !variants.length) return;

    const optionNames = getProductOptionNames();
    const { colorIndex, sizeIndex } = inferOptionIndexes(variants, optionNames);
    const availableColors = buildAvailableColorSet(variants, colorIndex);

    scheduleFix(variants, colorIndex, sizeIndex, availableColors);
    autoSwitchSizeWhenColorSelected(variants, colorIndex, sizeIndex);
    wireColorClickSync(variants, colorIndex, sizeIndex);

    const root = document.querySelector('.product__info-container, #MainContent, body');
    if (!root) return;

    const observer = new MutationObserver(() => {
      scheduleFix(variants, colorIndex, sizeIndex, availableColors);
    });

    observer.observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'disabled', 'aria-disabled', 'data-613-size-selected']
    });

    document.addEventListener('change', () => {
      scheduleFix(variants, colorIndex, sizeIndex, availableColors);
    });

    document.addEventListener(
      'click',
      () => {
        window.setTimeout(() => scheduleFix(variants, colorIndex, sizeIndex, availableColors), 30);
        window.setTimeout(() => scheduleFix(variants, colorIndex, sizeIndex, availableColors), 150);
      },
      true
    );

    [100, 400, 1000, 2000].forEach((ms) => {
      window.setTimeout(() => scheduleFix(variants, colorIndex, sizeIndex, availableColors), ms);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
